import Phaser from "phaser";
import { TILE } from "../constants";

type Graphics = Phaser.GameObjects.Graphics;

function bake(
  scene: Phaser.Scene,
  key: string,
  width: number,
  height: number,
  draw: (graphics: Graphics) => void,
): void {
  const graphics = scene.add.graphics().setVisible(false);
  draw(graphics);
  graphics.generateTexture(key, width, height);
  graphics.destroy();
}

function fill(
  graphics: Graphics,
  color: number,
  x: number,
  y: number,
  width: number,
  height: number,
  alpha = 1,
): void {
  graphics.fillStyle(color, alpha);
  graphics.fillRect(x, y, width, height);
}

function pixelNoise(
  graphics: Graphics,
  colors: readonly number[],
  count: number,
  minY = 0,
): void {
  let seed = 37;
  for (let index = 0; index < count; index += 1) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const x = seed % TILE;
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const y = minY + (seed % Math.max(1, TILE - minY));
    const color = colors[index % colors.length] ?? 0xffffff;
    fill(graphics, color, x, y, index % 3 === 0 ? 2 : 1, 1);
  }
}

export function createModernTextures(scene: Phaser.Scene): void {
  createTerrain(scene);
  createItems(scene);
  createPlayerBodies(scene);
  createEnemyBodies(scene);
  createEffects(scene);
}

function createTerrain(scene: Phaser.Scene): void {
  bake(scene, "tile-ground", TILE, TILE, (g) => {
    fill(g, 0x59352d, 0, 0, TILE, TILE);
    fill(g, 0x37242a, 0, 14, TILE, 10);
    fill(g, 0x2f6f46, 0, 0, TILE, 7);
    fill(g, 0x58c567, 0, 0, TILE, 3);
    fill(g, 0x8be26f, 2, 0, 8, 2);
    fill(g, 0x2f6f46, 5, 6, 3, 4);
    fill(g, 0x885243, 3, 11, 6, 4);
    fill(g, 0xbd7652, 4, 10, 4, 2);
    fill(g, 0x201a24, 15, 17, 7, 5);
    pixelNoise(g, [0x75463a, 0xa26048, 0x33232b], 10, 8);
  });
  bake(scene, "tile-dirt", TILE, TILE, (g) => {
    fill(g, 0x59352d, 0, 0, TILE, TILE);
    fill(g, 0x37242a, 0, 17, TILE, 7);
    fill(g, 0x885243, 3, 5, 7, 5);
    fill(g, 0xbd7652, 4, 4, 4, 2);
    fill(g, 0x201a24, 15, 11, 7, 5);
    pixelNoise(g, [0x75463a, 0xa26048, 0x33232b], 13, 0);
  });
  bake(scene, "tile-ground-blue", TILE, TILE, (g) => {
    fill(g, 0x172550, 0, 0, TILE, TILE);
    fill(g, 0x0c1434, 0, 17, TILE, 7);
    fill(g, 0x2855a7, 0, 0, TILE, 4);
    fill(g, 0x47d9ec, 0, 0, 11, 2);
    fill(g, 0x5f73c6, 4, 8, 7, 5);
    fill(g, 0xa87bea, 6, 7, 3, 2);
    pixelNoise(g, [0x29417e, 0x4764a7, 0x121b40], 10, 5);
  });
  bake(scene, "tile-dirt-blue", TILE, TILE, (g) => {
    fill(g, 0x172550, 0, 0, TILE, TILE);
    fill(g, 0x0c1434, 0, 17, TILE, 7);
    fill(g, 0x5f73c6, 4, 7, 7, 5);
    fill(g, 0xa87bea, 6, 6, 3, 2);
    pixelNoise(g, [0x29417e, 0x4764a7, 0x121b40], 13);
  });
  bake(scene, "tile-hard", TILE, TILE, (g) => {
    // Higher-contrast stone: light face, crisp bevel, dark mortar.
    fill(g, 0x0b0a10, 0, 0, TILE, TILE);
    fill(g, 0x4a4763, 1, 1, TILE - 2, TILE - 2);
    fill(g, 0x7f7c9c, 2, 2, TILE - 4, 3);
    fill(g, 0x7f7c9c, 2, 2, 3, TILE - 4);
    fill(g, 0x2a2839, 3, TILE - 5, TILE - 5, 3);
    fill(g, 0x2a2839, TILE - 5, 3, 3, TILE - 5);
    fill(g, 0x8c4652, 6, 15, 6, 3);
    fill(g, 0x9c99b8, 6, 6, 4, 4);
    fill(g, 0x1b1a26, 15, 9, 4, 4);
  });

  const brick = (
    key: string,
    base: number,
    shadow: number,
    light: number,
    mortar: number,
  ): void => {
    bake(scene, key, TILE, TILE, (g) => {
      fill(g, base, 0, 0, TILE, TILE);
      fill(g, mortar, 0, 10, TILE, 2);
      fill(g, mortar, 11, 0, 2, 10);
      fill(g, mortar, 5, 12, 2, 12);
      fill(g, light, 2, 2, 7, 2);
      fill(g, light, 14, 13, 7, 2);
      fill(g, shadow, 0, TILE - 3, TILE, 3);
      fill(g, shadow, TILE - 2, 0, 2, TILE);
    });
  };
  brick("tile-brick", 0xaa4b4f, 0x5f2935, 0xe77a67, 0x372334);
  brick("tile-brick-blue", 0x394a8c, 0x172250, 0x7184d2, 0x101638);

  bake(scene, "tile-question", TILE, TILE, (g) => {
    fill(g, 0x6d3d1f, 1, 2, 22, 21);
    g.fillStyle(0xf7b733, 1);
    g.fillRoundedRect(1, 0, 22, 21, 4);
    fill(g, 0xffe783, 4, 2, 14, 3);
    fill(g, 0xd67b22, 3, 17, 18, 3);
    fill(g, 0xfff3c4, 10, 5, 5, 3);
    fill(g, 0xfff3c4, 14, 8, 3, 4);
    fill(g, 0xfff3c4, 10, 11, 4, 3);
    fill(g, 0xfff3c4, 10, 16, 4, 3);
    fill(g, 0x784528, 3, 4, 2, 2);
    fill(g, 0x784528, 19, 4, 2, 2);
  });
  bake(scene, "tile-used", TILE, TILE, (g) => {
    fill(g, 0x3f3341, 1, 1, 22, 22);
    fill(g, 0x67505a, 3, 3, 18, 4);
    fill(g, 0x211d2b, 3, 18, 18, 3);
    fill(g, 0x9d7680, 5, 8, 3, 3);
    fill(g, 0x211d2b, 16, 13, 3, 3);
  });

  const pipe = (key: string, left: boolean, top: boolean): void => {
    bake(scene, key, TILE, TILE, (g) => {
      fill(g, 0x123b43, left ? 2 : 0, 0, left ? 22 : 20, TILE);
      fill(g, 0x1d7b71, left ? 3 : 0, 0, left ? 19 : 19, TILE);
      fill(g, 0x46e6b0, left ? 5 : 2, 0, 5, TILE);
      fill(g, 0xa3f7c7, left ? 6 : 3, 2, 2, TILE - 4);
      fill(g, 0x0d2c38, left ? 20 : 17, 0, 4, TILE);
      if (top) {
        fill(g, 0x144853, 0, 0, TILE, 5);
        fill(g, 0x39c79c, 1, 1, TILE - 2, 5);
        fill(g, 0x93f5c1, 4, 1, 5, 3);
        fill(g, 0x0d2c38, 20, 1, 4, 6);
      }
    });
  };
  pipe("tile-pipe-tl", true, true);
  pipe("tile-pipe-tr", false, true);
  pipe("tile-pipe-bl", true, false);
  pipe("tile-pipe-br", false, false);

  bake(scene, "tile-platform", TILE, TILE, (g) => {
    fill(g, 0x172132, 0, 7, TILE, 8);
    fill(g, 0x355947, 0, 3, TILE, 8);
    fill(g, 0x67d06f, 0, 1, TILE, 5);
    fill(g, 0xa8ee82, 2, 1, 10, 2);
    fill(g, 0x28523d, 6, 14, 4, 10);
    fill(g, 0x67d06f, 7, 15, 2, 6);
  });
  bake(scene, "tile-spring", TILE, TILE, (g) => {
    fill(g, 0x172132, 2, 18, 20, 5);
    fill(g, 0xffd34e, 2, 2, 20, 6);
    fill(g, 0xfff2a3, 4, 2, 12, 2);
    g.lineStyle(3, 0x64d9ec, 1);
    g.lineBetween(6, 8, 18, 18);
    g.lineBetween(18, 8, 6, 18);
  });
  bake(scene, "tile-spring-down", TILE, TILE, (g) => {
    fill(g, 0x172132, 2, 18, 20, 5);
    fill(g, 0xffd34e, 2, 12, 20, 6);
    fill(g, 0xfff2a3, 4, 12, 12, 2);
  });

  for (const frame of [1, 2]) {
    bake(scene, `tile-lava-${frame}`, TILE, TILE, (g) => {
      fill(g, 0x6d1424, 0, 7, TILE, 17);
      const offset = frame === 1 ? 0 : 6;
      fill(g, 0xff5e38, 0, 5, TILE, 8);
      fill(g, 0xffb52e, offset, 3, 12, 5);
      fill(g, 0xfff09a, (offset + 3) % TILE, 2, 6, 3);
      fill(g, 0xa92131, frame === 1 ? 15 : 2, 13, 7, 5);
    });
  }
  bake(scene, "tile-bridge", TILE, TILE, (g) => {
    fill(g, 0x291d29, 0, 9, TILE, 8);
    fill(g, 0x7b3b34, 0, 5, TILE, 8);
    fill(g, 0xcd7050, 2, 5, 14, 3);
    fill(g, 0x371b25, 0, 15, TILE, 3);
    fill(g, 0xf29b69, 4, 6, 3, 2);
  });
  bake(scene, "tile-axe", TILE, TILE, (g) => {
    fill(g, 0x69382d, 11, 7, 4, 17);
    g.fillStyle(0x56dfd0, 1);
    g.fillTriangle(3, 3, 20, 1, 13, 13);
    fill(g, 0xbafff4, 5, 3, 9, 2);
    fill(g, 0x1b7377, 13, 7, 5, 5);
  });
  bake(scene, "tile-flagpole", TILE, TILE, (g) => {
    fill(g, 0x17383e, 11, 0, 5, TILE);
    fill(g, 0x63ead9, 11, 0, 2, TILE);
  });
  bake(scene, "tile-flagball", TILE, TILE, (g) => {
    g.fillStyle(0xffd34e, 1);
    g.fillCircle(13, 8, 7);
    fill(g, 0xfff3a6, 10, 3, 5, 3);
    fill(g, 0x17383e, 11, 14, 5, 10);
  });
  bake(scene, "tile-castle", TILE, TILE, (g) => {
    fill(g, 0x0f0d17, 0, 0, TILE, TILE);
    fill(g, 0x55475e, 1, 1, 22, 22);
    fill(g, 0x8a6a78, 2, 2, 20, 4);
    fill(g, 0x2a2232, 1, 11, 22, 2);
    fill(g, 0x0f0d17, 4, 9, 6, 11);
    fill(g, 0x0f0d17, 15, 9, 5, 11);
    fill(g, 0xd25a4c, 6, 12, 2, 5);
    fill(g, 0xd25a4c, 17, 12, 2, 5);
  });
  bake(scene, "tile-door", TILE, TILE, (g) => {
    fill(g, 0x3e3345, 0, 0, TILE, TILE);
    g.fillStyle(0x100c14, 1);
    g.fillRoundedRect(5, 4, 14, 20, 7);
    fill(g, 0xffc94a, 15, 13, 2, 2);
  });
  bake(scene, "tile-hill", TILE, TILE, (g) => {
    g.fillStyle(0x2a8b67, 1);
    g.fillCircle(7, 20, 8);
    g.fillCircle(15, 16, 11);
    g.fillCircle(23, 21, 7);
    fill(g, 0x2a8b67, 0, 16, TILE, 8);
    g.fillStyle(0x68d477, 1);
    g.fillCircle(13, 15, 7);
    fill(g, 0x68d477, 5, 15, 13, 7);
    fill(g, 0xa9ee84, 10, 10, 4, 3);
  });
  bake(scene, "tile-bush", TILE, TILE, (g) => {
    g.fillStyle(0x29694f, 1);
    g.fillCircle(6, 16, 7);
    g.fillCircle(14, 12, 9);
    g.fillCircle(21, 17, 6);
    fill(g, 0x58c567, 5, 8, 10, 4);
    fill(g, 0xa7eb80, 8, 8, 4, 2);
  });
  bake(scene, "tile-cloud", TILE, TILE, (g) => {
    g.fillStyle(0xf9dbd0, 1);
    g.fillCircle(6, 14, 6);
    g.fillCircle(13, 9, 8);
    g.fillCircle(21, 15, 6);
    fill(g, 0xfff6db, 4, 10, 15, 6);
    fill(g, 0xeaa8bb, 5, 17, 16, 3);
  });
  createSmbDeco(scene);
  bake(scene, "tile-hidden", TILE, TILE, () => undefined);
  bake(scene, "tile-coin-deco", TILE, TILE, (g) => {
    g.fillStyle(0xffd34e, 1);
    g.fillEllipse(12, 12, 12, 21);
    fill(g, 0xfff3a6, 9, 4, 3, 14);
    fill(g, 0xb86a26, 15, 6, 2, 13);
  });
}

/** SMB-style flat sky colours per theme. */
export const SKY_COLORS = {
  overworld: 0x5c94fc,
  athletic: 0x5c94fc,
  underground: 0x000000,
  castle: 0x000000,
} as const;

/**
 * Composed 1985-style decorations: stepped hills with eye slits, three-lobed
 * bushes, outlined clouds and treetop trunks. Sized in whole tiles so the level
 * grids place them by run length. All are anchored bottom-left (origin 0,1).
 */
function createSmbDeco(scene: Phaser.Scene): void {
  const HILL = 0x00a800;
  const HILL_LINE = 0x0f3f10;
  const HILL_MARK = 0x000000;
  // Hills: run length 1..3 maps to widths 3..5 tiles and heights 2..4 tiles.
  for (const run of [1, 2, 3]) {
    const w = (run + 2) * TILE;
    const h = (run + 1) * TILE;
    bake(scene, `deco-hill-${run}`, w, h, (g) => {
      // Stepped silhouette: each tile-tall step narrows half a tile per side,
      // so the cap is always two tiles wide like the NES hill.
      const half = TILE / 2;
      for (let step = 0; step <= run; step += 1) {
        const y = h - (step + 1) * TILE;
        const x0 = step * half;
        const x1 = w - step * half;
        g.fillStyle(HILL_LINE, 1);
        if (step === run) {
          g.fillRoundedRect(x0, y, x1 - x0, TILE + 2, { tl: TILE, tr: TILE, bl: 0, br: 0 });
          g.fillStyle(HILL, 1);
          g.fillRoundedRect(x0 + 2, y + 2, x1 - x0 - 4, TILE, { tl: TILE - 2, tr: TILE - 2, bl: 0, br: 0 });
        } else {
          g.fillTriangle(x0, y + TILE, x0 + half, y, x0 + half, y + TILE);
          g.fillTriangle(x1, y + TILE, x1 - half, y, x1 - half, y + TILE);
          fill(g, HILL_LINE, x0 + half, y, x1 - x0 - TILE, TILE);
          g.fillStyle(HILL, 1);
          g.fillTriangle(x0 + 3, y + TILE, x0 + half, y + 3, x0 + half, y + TILE);
          g.fillTriangle(x1 - 3, y + TILE, x1 - half, y + 3, x1 - half, y + TILE);
          fill(g, HILL, x0 + half, y + 2, x1 - x0 - TILE, TILE - 2);
          fill(g, HILL, x0 + 3, y + TILE - 2, x1 - x0 - 6, 2);
        }
      }
      // Eye slits on the cap and the two marks on the lower slope.
      const cx = w / 2;
      fill(g, HILL_MARK, cx - 9, h - run * TILE - 16, 3, 7);
      fill(g, HILL_MARK, cx + 6, h - run * TILE - 16, 3, 7);
      fill(g, HILL_MARK, cx - TILE - 4, h - 13, 3, 6);
      fill(g, HILL_MARK, cx + TILE + 1, h - 13, 3, 6);
    });
  }
  // Bushes: run length 1..3 maps to widths 3..5 tiles, one tile tall.
  for (const run of [1, 2, 3]) {
    const w = (run + 2) * TILE;
    const h = TILE;
    bake(scene, `deco-bush-${run}`, w, h, (g) => {
      const lobes = run + 2;
      const r = TILE / 2;
      g.fillStyle(HILL_LINE, 1);
      for (let i = 0; i < lobes; i += 1) g.fillCircle(r + i * TILE, h - r + 1, r);
      fill(g, HILL_LINE, 2, h - 4, w - 4, 4);
      g.fillStyle(HILL, 1);
      for (let i = 0; i < lobes; i += 1) g.fillCircle(r + i * TILE, h - r + 1, r - 2);
      fill(g, HILL, 4, h - 6, w - 8, 4);
      g.fillStyle(0x80d010, 1);
      for (let i = 0; i < lobes; i += 1) g.fillCircle(r + i * TILE - 3, h - r - 3, 3);
    });
  }
  // Clouds: run length 2..4 cells maps to widths 2..4 tiles, two tiles tall.
  for (const run of [2, 3, 4]) {
    const w = run * TILE;
    const h = TILE * 2;
    bake(scene, `deco-cloud-${run}`, w, h, (g) => {
      const r = TILE / 2;
      const lobes = run;
      g.fillStyle(0x000000, 1);
      for (let i = 0; i < lobes; i += 1) g.fillCircle(r + i * TILE, TILE - 2, r);
      g.fillRoundedRect(0, TILE - 4, w, TILE + 2, 8);
      g.fillStyle(0xfcfcfc, 1);
      for (let i = 0; i < lobes; i += 1) g.fillCircle(r + i * TILE, TILE - 2, r - 2);
      g.fillRoundedRect(2, TILE - 2, w - 4, TILE - 2, 6);
      // Soft blue underside like the NES palette.
      g.fillStyle(0x9cd0ff, 1);
      g.fillRoundedRect(4, h - 10, w - 8, 6, 3);
    });
  }
  // Treetop trunk for 1-3, tiled downward from the platform.
  bake(scene, "deco-trunk", TILE, TILE, (g) => {
    fill(g, 0x0f3f10, 6, 0, 12, TILE);
    fill(g, 0x00a800, 8, 0, 8, TILE);
    fill(g, 0x80d010, 9, 0, 2, TILE);
  });
}

function createItems(scene: Phaser.Scene): void {
  for (const frame of [1, 2, 3, 4]) {
    bake(scene, `item-coin-${frame}`, 18, 24, (g) => {
      const widths = [14, 9, 4, 9];
      const width = widths[frame - 1] ?? 14;
      g.fillStyle(0xffd34e, 1);
      g.fillEllipse(9, 12, width, 22);
      fill(g, 0xfff4ae, 7, 3, Math.max(1, width / 4), 15);
      fill(g, 0xb86a26, 12, 6, Math.max(1, width / 5), 13);
    });
  }
  bake(scene, "item-coin", 18, 24, (g) => {
    g.fillStyle(0xffd34e, 1);
    g.fillEllipse(9, 12, 14, 22);
    fill(g, 0xfff4ae, 6, 3, 3, 15);
    fill(g, 0xb86a26, 12, 6, 2, 13);
  });
  const mushroom = (key: string, cap: number): void => {
    bake(scene, key, 30, 28, (g) => {
      g.fillStyle(0x1b1722, 1);
      g.fillRoundedRect(2, 2, 26, 15, 8);
      g.fillStyle(cap, 1);
      g.fillRoundedRect(3, 1, 24, 15, 8);
      fill(g, 0xfff1d0, 6, 4, 7, 6);
      fill(g, 0xfff1d0, 18, 3, 5, 5);
      g.fillStyle(0xf2c29f, 1);
      g.fillRoundedRect(8, 14, 14, 13, 4);
      fill(g, 0x251b25, 11, 19, 2, 3);
      fill(g, 0x251b25, 17, 19, 2, 3);
    });
  };
  mushroom("item-mushroom", 0xee4c5c);
  mushroom("item-1up", 0x46bf76);
  bake(scene, "item-flower", 30, 32, (g) => {
    fill(g, 0x2f9f66, 14, 17, 4, 15);
    fill(g, 0x66da75, 8, 22, 8, 5);
    fill(g, 0x66da75, 17, 25, 8, 5);
    g.fillStyle(0xf05a67, 1);
    g.fillCircle(15, 11, 10);
    fill(g, 0xffd34e, 8, 7, 14, 9);
    fill(g, 0xfff2b2, 11, 8, 4, 4);
    fill(g, 0x282035, 12, 12, 2, 2);
    fill(g, 0x282035, 18, 12, 2, 2);
  });
  bake(scene, "item-star", 30, 30, (g) => {
    g.fillStyle(0xffd34e, 1);
    g.fillPoints([
      new Phaser.Math.Vector2(15, 1),
      new Phaser.Math.Vector2(19, 10),
      new Phaser.Math.Vector2(29, 11),
      new Phaser.Math.Vector2(21, 18),
      new Phaser.Math.Vector2(24, 28),
      new Phaser.Math.Vector2(15, 22),
      new Phaser.Math.Vector2(6, 28),
      new Phaser.Math.Vector2(9, 18),
      new Phaser.Math.Vector2(1, 11),
      new Phaser.Math.Vector2(11, 10),
    ], true);
    fill(g, 0xfff3aa, 12, 5, 4, 9);
    fill(g, 0x2b2031, 10, 13, 2, 3);
    fill(g, 0x2b2031, 18, 13, 2, 3);
  });
  for (const frame of [1, 2]) {
    bake(scene, `item-fireball${frame === 1 ? "" : "-2"}`, 16, 16, (g) => {
      g.fillStyle(frame === 1 ? 0xff653f : 0xffbd38, 1);
      g.fillCircle(8, 8, 7);
      fill(g, 0xfff5b3, frame === 1 ? 5 : 7, 4, 5, 5);
      fill(g, 0xd82f48, frame === 1 ? 1 : 10, 7, 5, 4);
    });
  }
  bake(scene, "item-flame", 28, 18, (g) => {
    g.fillStyle(0xff5e38, 1);
    g.fillTriangle(0, 9, 10, 1, 28, 9);
    g.fillTriangle(0, 9, 12, 17, 28, 9);
    fill(g, 0xffc944, 8, 6, 16, 7);
    fill(g, 0xfff0a0, 15, 7, 8, 5);
  });
}

type PlayerPose =
  | "idle"
  | "walk"
  | "walk2"
  | "run"
  | "jump"
  | "skid"
  | "duck"
  | "dead";

function createPlayerBodies(scene: Phaser.Scene): void {
  const small: PlayerPose[] = [
    "idle",
    "walk",
    "walk2",
    "run",
    "jump",
    "skid",
    "dead",
  ];
  const large: PlayerPose[] = [
    "idle",
    "walk",
    "walk2",
    "run",
    "jump",
    "skid",
    "duck",
  ];
  for (const fire of [false, true]) {
    for (const pose of small) {
      bake(scene, `player-small-${fire ? "fire-" : ""}${pose}`, 48, 48, (g) => {
        drawPlayerBody(g, pose, false, fire);
      });
    }
    for (const pose of large) {
      bake(scene, `player-super-${fire ? "fire-" : ""}${pose}`, 48, 64, (g) => {
        drawPlayerBody(g, pose, true, fire);
      });
    }
  }
}

function drawPlayerBody(
  graphics: Graphics,
  pose: PlayerPose,
  large: boolean,
  fire: boolean,
): void {
  const bottom = large ? 63 : 47;
  const torsoTop = large ? 31 : 27;
  const shirt = fire ? 0xf6e3c5 : 0xe84f56;
  const shirtLight = fire ? 0xffffff : 0xff8580;
  const overalls = fire ? 0x43c8b6 : 0x385ec9;
  const overallsDark = fire ? 0x176c72 : 0x263b84;
  const boot = 0x362535;
  if (pose === "dead") {
    graphics.fillStyle(shirt, 1);
    graphics.fillRoundedRect(13, 30, 22, 11, 5);
    fill(graphics, overalls, 16, 38, 16, 7);
    fill(graphics, boot, 8, 42, 12, 5);
    fill(graphics, boot, 29, 42, 12, 5);
    return;
  }
  const duck = pose === "duck";
  const top = duck ? torsoTop + 12 : torsoTop;
  // Neck/collar so the head always lands on something solid.
  fill(graphics, 0xf0b58e, 20, top - 4, 8, 6);
  fill(graphics, shirt, 17, top - 1, 14, 4);
  graphics.fillStyle(shirt, 1);
  graphics.fillRoundedRect(10, top, 28, large && !duck ? 20 : 13, 7);
  fill(graphics, shirtLight, 13, top + 2, 10, 3);
  fill(graphics, overalls, 16, top + 8, 17, large && !duck ? 16 : 10);
  fill(graphics, overallsDark, 16, top + 17, 17, large && !duck ? 8 : 4);
  fill(graphics, 0xffd34e, 18, top + 9, 3, 3);
  fill(graphics, 0xffd34e, 28, top + 9, 3, 3);
  fill(graphics, shirt, pose === "skid" ? 3 : 6, top + 5, 10, 7);
  fill(graphics, shirt, pose === "skid" ? 35 : 33, top + 6, 10, 7);
  fill(graphics, 0xf0b58e, pose === "skid" ? 1 : 4, top + 8, 6, 6);
  fill(graphics, 0xf0b58e, pose === "skid" ? 41 : 39, top + 9, 6, 6);

  if (duck) {
    fill(graphics, overallsDark, 12, bottom - 8, 25, 5);
    fill(graphics, boot, 9, bottom - 5, 30, 5);
    return;
  }
  const step = pose === "walk" || pose === "walk2";
  const alternateStep = pose === "walk2";
  const running = pose === "run";
  const jump = pose === "jump";
  const leftX = jump
    ? 8
    : running
      ? 7
      : step
        ? alternateStep
          ? 17
          : 11
        : 14;
  const rightX = jump
    ? 29
    : running
      ? 32
      : step
        ? alternateStep
          ? 23
          : 28
        : 26;
  fill(graphics, overallsDark, leftX + 3, bottom - 13, 8, 9);
  fill(graphics, overallsDark, rightX, bottom - 13, 8, 9);
  fill(graphics, boot, leftX, bottom - 6, 13, 6);
  fill(graphics, boot, rightX, bottom - 6, 13, 6);
  fill(graphics, 0x5b4050, leftX + 2, bottom - 5, 8, 2);
  fill(graphics, 0x5b4050, rightX + 2, bottom - 5, 8, 2);
}

function createEnemyBodies(scene: Phaser.Scene): void {
  for (const frame of [1, 2]) {
    // Bodies are one tile wide (like the 1985 stompers), centred on the
    // 56px canvas so the physics offsets in Enemy.setupBody stay valid.
    bake(scene, `enemy-walker-${frame}`, 56, 56, (g) => {
      g.fillStyle(0x1a1526, 1);
      g.fillRoundedRect(13, 28, 30, 24, 9);
      g.fillStyle(0x6f334d, 1);
      g.fillRoundedRect(15, 30, 26, 20, 8);
      fill(g, 0xc85160, 18, 31, 20, 5);
      fill(g, 0x41263a, 18, 42, 20, 6);
      const shift = frame === 1 ? 0 : 3;
      fill(g, 0x1a1526, 12 + shift, 47, 15, 9);
      fill(g, 0x1a1526, 29 - shift, 47, 15, 9);
      fill(g, 0x3a2f45, 14 + shift, 48, 11, 5);
      fill(g, 0x3a2f45, 31 - shift, 48, 11, 5);
      fill(g, 0xffd34e, 26, 36, 5, 4);
    });
    bake(scene, `enemy-troopa-${frame}`, 56, 72, (g) => {
      g.fillStyle(0x1a1526, 1);
      g.fillRoundedRect(14, 32, 28, 32, 12);
      g.fillStyle(0x2d8c68, 1);
      g.fillRoundedRect(16, 34, 24, 28, 11);
      g.fillStyle(0xa8f5a0, 1);
      g.fillRoundedRect(18, 36, 20, 7, 3);
      fill(g, 0x61d878, 19, 42, 18, 13);
      fill(g, 0x1b5a52, 23, 44, 10, 11);
      fill(g, 0xffe0aa, 11, 50, 6, 9);
      fill(g, 0xffe0aa, 39, 50, 6, 9);
      const shift = frame === 1 ? 0 : 3;
      fill(g, 0x1a1526, 13 + shift, 61, 14, 11);
      fill(g, 0x1a1526, 29 - shift, 61, 14, 11);
      fill(g, 0x302238, 15 + shift, 62, 10, 7);
      fill(g, 0x302238, 31 - shift, 62, 10, 7);
    });
    // Native 24x36 piranha (1 x 1.5 tiles) so it is displayed unscaled.
    bake(scene, `enemy-plant-${frame}`, 24, 36, (g) => {
      fill(g, 0x0f3a37, 9, 16, 6, 20);
      fill(g, 0x43c478, 10, 16, 2, 20);
      const top = frame === 1 ? 4 : 1;
      g.fillStyle(0x1a1526, 1);
      g.fillRoundedRect(0, top - 1, 24, 20, 8);
      g.fillStyle(0xb73f5b, 1);
      g.fillRoundedRect(1, top, 22, 18, 7);
      fill(g, 0xf17a6f, 3, top + 1, 18, 3);
      fill(g, 0x2a0d19, 3, top + 7, 18, frame === 1 ? 6 : 9);
      for (const x of [4, 9, 14, 19]) {
        fill(g, 0xfff6e0, x, top + 7, 2, 2);
        fill(g, 0xfff6e0, x, top + (frame === 1 ? 11 : 14), 2, 2);
      }
      fill(g, 0xffe4ba, 2, top + 3, 3, 2);
      fill(g, 0xffe4ba, 19, top + 3, 3, 2);
      // Side leaves.
      fill(g, 0x43c478, 2, 24, 7, 3);
      fill(g, 0x43c478, 15, 24, 7, 3);
    });
  }
  bake(scene, "enemy-walker-flat", 56, 24, (g) => {
    g.fillStyle(0x1a1526, 1);
    g.fillRoundedRect(12, 10, 32, 14, 6);
    g.fillStyle(0x6f334d, 1);
    g.fillRoundedRect(14, 12, 28, 10, 5);
    fill(g, 0xc85160, 17, 12, 22, 3);
    fill(g, 0x1a1526, 15, 19, 26, 5);
  });
  bake(scene, "enemy-shell", 56, 44, (g) => {
    g.fillStyle(0x1a1526, 1);
    g.fillRoundedRect(12, 18, 32, 25, 12);
    g.fillStyle(0x175452, 1);
    g.fillRoundedRect(14, 20, 28, 21, 10);
    g.fillStyle(0x3bc67a, 1);
    g.fillRoundedRect(16, 18, 24, 20, 10);
    fill(g, 0xb9ffa8, 19, 21, 18, 5);
    fill(g, 0x195d54, 22, 28, 12, 9);
    fill(g, 0xffdfac, 13, 37, 30, 5);
  });
  bake(scene, "enemy-boss", 112, 112, (g) => {
    g.fillStyle(0x182d32, 1);
    g.fillRoundedRect(8, 43, 96, 59, 25);
    fill(g, 0x267257, 13, 45, 84, 39);
    fill(g, 0x55c26d, 20, 48, 65, 10);
    fill(g, 0x7b3548, 22, 66, 69, 28);
    for (const x of [18, 39, 62, 83]) {
      g.fillStyle(0xffcf53, 1);
      g.fillTriangle(x, 46, x + 7, 31, x + 13, 47);
    }
    fill(g, 0x251c2b, 10, 96, 34, 15);
    fill(g, 0x251c2b, 69, 96, 34, 15);
    fill(g, 0xf3d09d, 0, 72, 20, 17);
    fill(g, 0xf3d09d, 92, 72, 20, 17);
  });
  bake(scene, "enemy-podoboo-1", 26, 32, (g) => {
    g.fillStyle(0xff553f, 1);
    g.fillEllipse(13, 16, 23, 30);
    fill(g, 0xffbe38, 7, 6, 12, 19);
    fill(g, 0xfff1a0, 10, 8, 5, 11);
  });
  bake(scene, "enemy-podoboo-2", 26, 32, (g) => {
    g.fillStyle(0xff6b3d, 1);
    g.fillEllipse(13, 16, 19, 30);
    fill(g, 0xffd344, 5, 10, 16, 14);
    fill(g, 0xfff1a0, 11, 7, 5, 12);
  });
}

function createEffects(scene: Phaser.Scene): void {
  bake(scene, "fx-particle", 8, 8, (g) => {
    g.fillStyle(0xfff2b4, 1);
    g.fillCircle(4, 4, 4);
  });
  bake(scene, "fx-mote", 4, 4, (g) => {
    fill(g, 0xffffff, 1, 1, 2, 2, 0.8);
  });
  bake(scene, "fx-dust", 18, 10, (g) => {
    g.fillStyle(0xd9c8a7, 0.8);
    g.fillCircle(4, 7, 3);
    g.fillCircle(9, 5, 4);
    g.fillCircle(14, 7, 3);
  });
  bake(scene, "fx-spark", 16, 16, (g) => {
    fill(g, 0xfff2a8, 7, 0, 2, 16);
    fill(g, 0xfff2a8, 0, 7, 16, 2);
    fill(g, 0xffffff, 5, 5, 6, 6);
  });
  bake(scene, "fx-brickbit", 9, 9, (g) => {
    fill(g, 0xb55052, 0, 0, 9, 9);
    fill(g, 0xf17a67, 1, 1, 6, 3);
    fill(g, 0x5c2935, 0, 7, 9, 2);
  });
  bake(scene, "ui-panel", 24, 24, (g) => {
    g.fillStyle(0x11152e, 0.92);
    g.fillRoundedRect(0, 0, 24, 24, 6);
    g.lineStyle(2, 0x66e0d1, 1);
    g.strokeRoundedRect(1, 1, 22, 22, 6);
  });
  bake(scene, "touch-btn", 64, 64, (g) => {
    g.fillStyle(0x10162d, 0.7);
    g.fillCircle(32, 32, 29);
    g.lineStyle(3, 0x72e2d1, 0.85);
    g.strokeCircle(32, 32, 27);
    g.fillStyle(0xffffff, 0.12);
    g.fillCircle(25, 24, 12);
  });
}
